/**
 * WhatsAppMeetModal
 * Crea un Google Meet e invia il link in chat.
 * Due modalità: "Adesso" (meeting istantaneo) e "Pianifica" (con data/ora).
 */

import { useState, useCallback, useEffect } from 'react';
import { X, Video, Calendar, Send, Loader2, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useCalendarStore } from '@/lib/stores/calendarStore';
import { useSettingsStore } from '@/lib/stores/settingsStore';

type MeetProvider = 'google_meet' | 'microsoft_teams';

interface WhatsAppMeetModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatName: string;
}

type Mode = 'now' | 'schedule';

export function WhatsAppMeetModal({ isOpen, onClose, chatName }: WhatsAppMeetModalProps) {
  const [mode, setMode] = useState<Mode>('now');
  const [title, setTitle] = useState(`Video call con ${chatName}`);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [durationMin, setDurationMin] = useState(60);
  const [loading, setLoading] = useState(false);
  const [meetLink, setMeetLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Provider selezionato + disponibilità (Google Meet / Microsoft Teams)
  const [provider, setProvider] = useState<MeetProvider>('google_meet');
  const [microsoftConnected, setMicrosoftConnected] = useState(false);

  const { sendTextMessage } = useWhatsAppChatStore();
  const createMicrosoftEvent = useCalendarStore((s) => s.createMicrosoftEvent);
  const { settings } = useSettingsStore();
  const teamsEnabled = !!(settings as any)?.microsoft_sync_teams;
  const teamsAvailable = microsoftConnected && teamsEnabled;
  // Google resta sempre disponibile nel modale WhatsApp (comportamento attuale):
  // il selettore compare solo quando anche Teams è disponibile.
  const showProviderSelector = teamsAvailable;

  // All'apertura, rileva account collegati e imposta il provider di default.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled || !userData.user) return;
      const { data } = await supabase
        .from('user_profiles')
        .select('google_account_connected, microsoft_account_connected')
        .eq('id', userData.user.id)
        .single();
      if (cancelled || !data) return;
      const g = !!(data as any).google_account_connected;
      const m = !!(data as any).microsoft_account_connected;
      setMicrosoftConnected(m);
      // Default: Google se disponibile, altrimenti Teams se disponibile, altrimenti Google (fallback).
      setProvider(g ? 'google_meet' : (m && teamsEnabled ? 'microsoft_teams' : 'google_meet'));
    })();
    return () => { cancelled = true; };
  }, [isOpen, teamsEnabled]);

  const handleCreate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startTime = mode === 'now' ? new Date() : new Date(date);
      const endTime = new Date(startTime.getTime() + durationMin * 60 * 1000);
      const eventTitle = mode === 'now' ? `Video call con ${chatName}` : title;
      const when = mode === 'now'
        ? 'adesso'
        : `il ${new Date(date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })} alle ${new Date(date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;

      // ── Microsoft Teams: crea evento Outlook dell'utente corrente con meeting Teams ──
      if (provider === 'microsoft_teams') {
        const result = await createMicrosoftEvent(
          undefined,
          {
            title: eventTitle,
            description: `Video call WhatsApp con ${chatName}`,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            all_day: false,
            attendees: [],
          } as any,
          { createTeamsMeeting: true },
        );
        if (!result) throw new Error('Errore creazione Teams. Verifica che Microsoft Calendar sia collegato.');
        const link = result.joinUrl;
        if (!link) throw new Error('Link Teams non ricevuto da Microsoft. Riprova.');

        setMeetLink(link);
        await sendTextMessage(
          `📹 *${eventTitle}*

Ti invito a una videochiamata Microsoft Teams${mode === 'schedule' ? ` ${when}` : ''}.

🔗 ${link}`
        );
        return;
      }

      // ── Google Meet (comportamento esistente) ──
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Non autenticato');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      if (!profile?.organization_id) throw new Error('Organizzazione non trovata');

      const { data, error: fnError } = await supabase.functions.invoke('google-calendar-modify', {
        body: {
          operation: 'create',
          organization_id: profile.organization_id,
          event: {
            title: eventTitle,
            description: `Video call WhatsApp con ${chatName}`,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            all_day: false,
            attendees: [],
            conference_data: {
              createRequest: {
                requestId: `meet-wa-${Date.now()}`,
                conferenceSolutionKey: { type: 'hangoutsMeet' },
              },
            },
          },
        },
      });

      if (fnError) throw new Error('Errore creazione Meet. Verifica che Google Calendar sia collegato.');

      const entryPoints = data?.event?.conferenceData?.entryPoints || [];
      const videoEntry = entryPoints.find((ep: any) => ep.entryPointType === 'video');
      const link = videoEntry?.uri;
      if (!link) throw new Error('Link Meet non ricevuto da Google. Riprova.');

      setMeetLink(link);

      // Invia in chat
      await sendTextMessage(
        `📹 *${eventTitle}*

Ti invito a una videochiamata Google Meet${mode === 'schedule' ? ` ${when}` : ''}.

🔗 ${link}`
      );
    } catch (err: any) {
      setError(err.message || 'Errore imprevisto');
    } finally {
      setLoading(false);
    }
  }, [mode, date, durationMin, title, chatName, sendTextMessage, provider, createMicrosoftEvent]);

  const handleClose = () => {
    setMeetLink(null);
    setError(null);
    setMode('now');
    setTitle(`Video call con ${chatName}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={!loading ? handleClose : undefined} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <Video className="w-5 h-5 text-blue-500" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">
            {provider === 'microsoft_teams' ? 'Microsoft Teams' : 'Google Meet'}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <button
            onClick={() => setMode('now')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              mode === 'now'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-[#787774] hover:text-[#37352F] dark:hover:text-white'
            }`}
          >
            <Send className="w-3.5 h-3.5" />
            Invia subito
          </button>
          <button
            onClick={() => setMode('schedule')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              mode === 'schedule'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-[#787774] hover:text-[#37352F] dark:hover:text-white'
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            Pianifica
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {meetLink ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
              <p className="text-sm font-medium text-[#37352F] dark:text-white">
                Link inviato in chat!
              </p>
              <a
                href={meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 underline break-all"
              >
                {meetLink}
              </a>
              <button
                onClick={handleClose}
                className="mt-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Chiudi
              </button>
            </div>
          ) : (
            <>
              {showProviderSelector && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[#787774]">Piattaforma</label>
                  <div className="flex gap-2">
                    {([
                      { value: 'google_meet' as MeetProvider, label: 'Google Meet' },
                      { value: 'microsoft_teams' as MeetProvider, label: 'Microsoft Teams' },
                    ]).map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setProvider(opt.value)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          provider === opt.value
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-[rgba(55,53,47,0.12)] dark:border-white/10 text-[#787774] hover:border-blue-400'
                        }`}
                      >
                        <Video className="w-3 h-3" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {mode === 'now' ? (
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-[#787774]">
                    Crea un meeting istantaneo e invia il link a <span className="font-medium text-[#37352F] dark:text-white">{chatName}</span>.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-[#787774]">Titolo</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-[#787774]">Data e ora</label>
                    <input
                      type="datetime-local"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-blue-400"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-[#787774]">Durata</label>
                    <div className="flex gap-2">
                      {[30, 60, 90, 120].map((min) => (
                        <button
                          key={min}
                          onClick={() => setDurationMin(min)}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            durationMin === min
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-[rgba(55,53,47,0.12)] dark:border-white/10 text-[#787774] hover:border-blue-400'
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          {min < 60 ? `${min}m` : `${min / 60}h`}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={loading || (mode === 'schedule' && !title.trim())}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creazione in corso...
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    {provider === 'microsoft_teams' ? 'Crea Teams e invia' : 'Crea Meet e invia'}
                  </>
                )}
              </button>

              <p className="text-[10px] text-[#787774] text-center">
                {provider === 'microsoft_teams'
                  ? 'Richiede Microsoft Calendar collegato nelle impostazioni'
                  : 'Richiede Google Calendar collegato nelle impostazioni'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
